import React, { memo, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import PivotChart from '../components/partials/PivotChart';
import css from './dashboard.css';
import { getReportTypeFromDataType, normalizeDimensionNames } from './utils';
import { useFetchTableData } from './hooks';
import { ACTION_TYPES } from '../components/partials/ChartWidgetWrapper/consts';
import ChartWidgetWrapper from '../components/partials/ChartWidgetWrapper';
import { DataTypes } from './widgetDataTypes';
import { UNIFIED_REPORT_TYPES_TO_DISPLAY_NAME } from '../components/pages/reports/consts';
import { getBlockedWidgetFilterNames } from './universalFiltersConfig';
import { CHART_TYPES, OPERATOR_TYPES, PIE_CHART, BAR_CHART } from '../utils/dashboardConsts';
import { buildOpenInReportsUrl } from './openInReports';
import { trackMixpanelEvent, REPORTS_EVENT_PREFIX } from '../utils/general';

function PivotChartWidget({
    dashboardId,
    dispatchWidgetVersions,
    dispatchWidgetsLoading,
    forceRefreshCounter,
    widget,
    globalFilters = [],
    height,
    width,
    onMenuClick = () => { },
    onCloneClick = () => { },
    onDeleteClick = () => { },
    startDate,
    endDate,
    updatingGraphQL = false,
    allowedActions = Object.values(ACTION_TYPES),
    displayUnenriched = false,
    apiFields = null,
    readOnly = false,
}) {
    const reportType = getReportTypeFromDataType(widget.dataType, widget.type);
    const [loading, tableData, error] = useFetchTableData(
        dashboardId,
        widget.gridParams.i,
        widget.query,
        globalFilters,
        startDate,
        endDate,
        null,
        null,
        reportType,
        dispatchWidgetVersions,
        dispatchWidgetsLoading,
        false,
        widget.dataType,
        displayUnenriched,
        updatingGraphQL,
        forceRefreshCounter,
        undefined,
        widget.type,
        widget.options
    );
    const chartRef = useRef(null);

    const handleExportClick = () => {
        if (!chartRef.current?.chart) {
            return;
        }

        const chart = chartRef.current.chart;
        const widgetTitle = widget.name;
        const filename = widgetTitle || 'chart';

        const exportOptions = {
            filename,
        };

        const isPieChartWidget = widget.type === PIE_CHART;

        const overriddenChartOptions = {
            title: { text: widgetTitle },
            // Enable data labels only for the exported image so values are visible
            plotOptions: isPieChartWidget
                ? {
                    pie: {
                        dataLabels: {
                            enabled: true,
                            useHTML: true,
                            distance: 20,
                            style: {
                                fontSize: '11px',
                                fontWeight: 'normal',
                                textOutline: 'none',
                                color: '#111827',
                            },
                            formatter() {
                                const name = this.point && this.point.name;
                                const value = this.y;
                                const percentage = this.percentage;
                                const tooltipOptions = this.series.tooltipOptions || {};
                                const valuePrefix = tooltipOptions.valuePrefix || '';
                                const valueSuffix = tooltipOptions.valueSuffix || '';
                                const valueDecimals = tooltipOptions.valueDecimals || 0;

                                let valueText = '';
                                if (typeof value === 'number') {
                                    valueText = `${valuePrefix}${value.toFixed(valueDecimals)}${valueSuffix}`;
                                }

                                let percentageText = '';
                                if (typeof percentage === 'number') {
                                    percentageText = `${percentage.toFixed(1)}%`;
                                }

                                const details = [valueText, percentageText].filter(Boolean).join(' · ');
                                const baseLabel = name || '';

                                if (!baseLabel && !details) {
                                    return '';
                                }

                                if (!details) {
                                    return `<span>${baseLabel}</span>`;
                                }

                                return `<span style="font-weight:600">${baseLabel}</span><br/><span>${details}</span>`;
                            },
                        },
                    },
                }
                : {
                    series: {
                        dataLabels: {
                            enabled: true,
                            style: {
                                fontSize: '11px',
                                fontWeight: 'normal',
                                textOutline: 'none',
                                color: '#111827',
                            },
                            formatter() {
                                const tooltipOptions = this.series.tooltipOptions || {};
                                const valuePrefix = tooltipOptions.valuePrefix || '';
                                const valueSuffix = tooltipOptions.valueSuffix || '';
                                const valueDecimals = tooltipOptions.valueDecimals || 0;
                                const value = this.y;

                                if (typeof value !== 'number') {
                                    return '';
                                }

                                return `${valuePrefix}${value.toFixed(valueDecimals)}${valueSuffix}`;
                            },
                        },
                    },
                },
        };

        const exportFn = chart.exportChartLocal ?? chart.exportChart;
        if (typeof exportFn === 'function') {
            exportFn.call(chart, exportOptions, overriddenChartOptions);
        }
    };

    const reportColumns = useMemo(() => {
        if (loading || !tableData || !tableData.columnDefs) {
            return null;
        }

        const columns = tableData.columnDefs.map(x => {
            if (
                widget.dataType === DataTypes.UNIFIED &&
                UNIFIED_REPORT_TYPES_TO_DISPLAY_NAME[x.context?.unifiedReportType]
            ) {
                x.headerName = `${UNIFIED_REPORT_TYPES_TO_DISPLAY_NAME[x.context.unifiedReportType]} - ${x.headerName}`;
            }
            return {
                name: x.field,
                fieldType: x.type,
                displayName: x.headerName,
                displayFormat: x.context?.displayFormat,
                unifiedReportType: x.context?.unifiedReportType,
            };
        });

        return columns;
    }, [loading, tableData]);

    const pivotChartSettings = useMemo(() => {
        if (!reportColumns) {
            return null;
        }

        const dimensions = widget.type !== PIE_CHART ? widget.options.rows : widget.query.dimensions;

        const skanDateDimensionName = widget.query.skanDateDimensionName;
        const xAxis = normalizeDimensionNames(dimensions, widget.dataType, skanDateDimensionName).filter(x =>
            reportColumns.find(c => c.name === x)
        );
        const yAxis = normalizeDimensionNames(widget.options.columns, widget.dataType, skanDateDimensionName).filter(x =>
            reportColumns.find(c => c.name === x)
        );
        return {
            chartType: widget.type,
            selectedMetric: widget.query.metrics[0],
            xAxis,
            yAxis,
            axis: widget.options.axis || null,
        };
    }, [reportColumns, widget.type, widget.query.metrics, widget.query.dimensions, widget.options, widget.query.skanDateDimensionName]);

    const noData = tableData == null || tableData.rowData == null || tableData.rowData.length === 0;
    const withTopX =
        [PIE_CHART, BAR_CHART].includes(widget.type) && (widget.metricForTopX || pivotChartSettings?.selectedMetric);

    const onOpenInReportsClick = () => {
        const redirectUrl = buildOpenInReportsUrl(widget, { startDate, endDate }, displayUnenriched);
        trackMixpanelEvent(REPORTS_EVENT_PREFIX, 'Open widget in reports clicked', {
            widget_query: widget.query,
        });
        window.open(redirectUrl, '_blank');
    };
    return (
        <ChartWidgetWrapper
            apiFields={apiFields}
            widgetTitle={widget.name}
            widgetType={widget.type}
            widgetDataType={widget.dataType}
            widgetFilters={widget.query.filters}
            showSpinner={loading}
            onMenuClick={onMenuClick}
            onDeleteClick={onDeleteClick}
            onCloneClick={onCloneClick}
            showNoData={!loading && !error && noData}
            blockedFilters={getBlockedWidgetFilterNames(widget.dataType, globalFilters)}
            showError={!loading && !!error}
            allowedActions={allowedActions}
            onExportClick={handleExportClick}
            onOpenInReportsClick={onOpenInReportsClick}
            withTopX={withTopX}
            readOnly={readOnly}
        >
            {!loading && !error && !noData && (
                <PivotChart
                    reportId={tableData.reportId}
                    rowData={tableData.rowData}
                    reportColumns={reportColumns}
                    showControls={false}
                    disableHiding
                    height={height - 20} // 20 = pivotChartWidget top(10)+bottom(10) padding
                    width={width - 20} // 20 = pivotChartWidget left(10)+right(10) padding
                    containerClass={css.pivotChartWidget}
                    settings={pivotChartSettings}
                    controlled
                    metricForTopX={widget.metricForTopX}
                    widgetName={widget.name}
                    withTopX={withTopX}
                    isTopXHeader={!allowedActions.length}
                    chartRef={chartRef}
                />
            )}
        </ChartWidgetWrapper>
    );
}

PivotChartWidget.propTypes = {
    dispatchWidgetVersions: PropTypes.func.isRequired,
    dispatchWidgetsLoading: PropTypes.func.isRequired,
    dashboardId: PropTypes.string.isRequired,
    widget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        dataType: PropTypes.string.isRequired,
        type: PropTypes.oneOf(CHART_TYPES.map(x => x.name)).isRequired,
        query: PropTypes.shape({
            dimensions: PropTypes.arrayOf(PropTypes.string),
            metrics: PropTypes.arrayOf(PropTypes.string),
            filters: PropTypes.arrayOf(
                PropTypes.shape({
                    field: PropTypes.string.isRequired,
                    operator: PropTypes.oneOf(OPERATOR_TYPES.map(x => x.name)).isRequired,
                    values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
                })
            ),
        }).isRequired,
        options: PropTypes.shape({
            rows: PropTypes.arrayOf(PropTypes.string),
            columns: PropTypes.arrayOf(PropTypes.string),
            axis: PropTypes.shape({
                sides: PropTypes.objectOf(PropTypes.oneOf(['left', 'right'])),
                left: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
                right: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
                range: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
            }),
        }).isRequired,
        metricForTopX: PropTypes.string,
    }).isRequired,
    globalFilters: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            operator: PropTypes.oneOf(OPERATOR_TYPES.map(x => x.name)).isRequired,
            values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
        })
    ),
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number,
    onMenuClick: PropTypes.func,
    onCloneClick: PropTypes.func,
    onDeleteClick: PropTypes.func,
    updatingGraphQL: PropTypes.bool,
    allowedActions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ACTION_TYPES))),
    displayUnenriched: PropTypes.bool,
    apiFields: PropTypes.objectOf(PropTypes.any),
    readOnly: PropTypes.bool,
};

export default memo(PivotChartWidget);
